/**
 * Centralized analytics event layer (Growth Platform — Slice S1).
 *
 * ONE place to emit business/conversion events. Everything funnels through
 * `track()`, which forwards to the existing Google Analytics 4 property
 * (gtag, configured in public/index.html: G-NW7FJNDDSZ).
 *
 * Why this exists:
 *  - Do not scatter raw gtag() calls across components (spec §35: "Do not
 *    duplicate tracking scripts").
 *  - Give every future engine (loyalty, membership, referral, MERN /api)
 *    a single, stable event vocabulary to emit against.
 *  - Fail safe: if gtag is unavailable (ad-blocker, SSR, tests), calls are
 *    silent no-ops and never throw.
 *
 * When the MERN backend lands, add a second sink here (POST to
 * /api/events) alongside gtag — call sites never change.
 */

// Canonical event names. Import EVENTS.* rather than typing string literals
// so the vocabulary stays consistent and greppable.
export const EVENTS = {
  PHONE_CALL_CLICKED: "phone_call_clicked",
  DIRECTIONS_CLICKED: "directions_clicked",
  WHATSAPP_CLICKED: "whatsapp_clicked",
  REPAIR_QUOTE_STARTED: "repair_quote_started",
  REPAIR_QUOTE_STEP: "repair_quote_step",
  REPAIR_QUOTE_COMPLETED: "repair_quote_completed",
  REPAIR_QUOTE_FAILED: "repair_quote_failed",
  CONTACT_FORM_SUBMITTED: "contact_form_submitted",
  PROMOTION_VIEWED: "promotion_viewed",
  HEALTH_CHECK_LEAD: "health_check_lead",
  BUSINESS_ACCOUNT_LEAD: "business_account_lead",
  MEMBERSHIP_VIEWED: "membership_viewed",
  MEMBERSHIP_JOIN_STARTED: "membership_join_started",
  MEMBERSHIP_WAITLIST_LEAD: "membership_waitlist_lead",
};

const isBrowser = typeof window !== "undefined";
const isDev = process.env.NODE_ENV === "development";

/**
 * Emit an analytics event.
 * @param {string} eventName - Prefer a value from EVENTS.
 * @param {Object} [params] - Flat key/value params (GA4 event params).
 */
export function track(eventName, params = {}) {
  if (!eventName) return;

  if (isDev) {
    // Visible in dev so we can confirm wiring without opening GA.
    // eslint-disable-next-line no-console
    console.debug("[analytics]", eventName, params);
  }

  if (!isBrowser || typeof window.gtag !== "function") return;

  try {
    window.gtag("event", eventName, params);
  } catch (err) {
    // Never let analytics break the UI.
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn("[analytics] failed", err);
    }
  }
}

/**
 * Convenience helpers for the highest-value store-conversion signals.
 * These wrap track() so intent reads clearly at the call site.
 */
export const trackPhoneCall = (source) =>
  track(EVENTS.PHONE_CALL_CLICKED, { source });

export const trackDirections = (source) =>
  track(EVENTS.DIRECTIONS_CLICKED, { source });

export const trackWhatsApp = (source) =>
  track(EVENTS.WHATSAPP_CLICKED, { source });
